import * as React from 'react'

import { Box, Button, Form, Text } from '@factorialco/deprecated-design-system'
import { useFormik } from 'formik'
import { GetEvaluationFormQuery } from 'generated/resources/graphql'
import { isEmpty } from 'lodash'

type AtsEvaluationFormQuestion = NonNullable<
  NonNullable<
    NonNullable<GetEvaluationFormQuery['ats']>['evaluationForm']
  >['questions']
>[0]

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { type EvaluationFormQuestionError } from '../useEvaluationFormForm'

interface QuestionItemProps {
  value: AtsEvaluationFormQuestion
  onEdit: (newValue: AtsEvaluationFormQuestion) => void
  onDelete: () => void
  errors: EvaluationFormQuestionError
  isLastQuestion: boolean
}

export const QuestionItem = ({
  value,
  onEdit,
  onDelete,
  errors,
  isLastQuestion,
}: QuestionItemProps) => {
  const isNew = isEmpty(value.text)
  const [isEditing, setIsEditing] = React.useState(isNew)
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)

  if (isEditing) {
    return (
      <QuestionForm
        value={value}
        onChange={(newValue) => {
          setIsEditing(false)
          onEdit(newValue)
        }}
        onCancel={() => (isNew ? onDelete() : setIsEditing(false))}
        errors={errors}
        isLastQuestion={isLastQuestion}
        isNew={isNew}
      />
    )
  }

  return (
    <Box
      flexDirection="row"
      alignItems="flexStart"
      gap="s8"
      background="grey100"
      borderRadius={{ all: 'abs008' }}
      border={{ all: { width: 's1', color: 'grey400', style: 'solid' } }}
      paddingX="s16"
      paddingY="s8"
    >
      <Box flexGrow>
        <Text.Normal color="grey1000" weight="medium">
          {value.text}
        </Text.Normal>
        <Text.Normal>{value.description}</Text.Normal>
      </Box>

      <Box flexDirection="row" alignItems="center" gap="s8" paddingY="s8">
        <Button
          type="secondary"
          onClick={() => setIsEditing(true)}
          aria-label={i18n.t('ats.evaluation_forms.form.edit_skill', { name: value.text })}
          iconName="pencil"
        />
        <Button
          type="secondary"
          onClick={() => setDeleteModalOpen(true)}
          aria-label={i18n.t('ats.evaluation_forms.form.delete_skill.delete', { name: value.text })}
          iconName="delete"
        />
      </Box>

      {deleteModalOpen && (
        <Modal
          illustration="delete"
          title={i18n.t('ats.evaluation_forms.form.delete_skill.confirm_title')}
          onClose={() => setDeleteModalOpen(false)}
          onSubmit={onDelete}
          footer={
            <Box flexDirection="row" gap="s16">
              <Button type="secondary" onClick={() => setDeleteModalOpen(false)}>
                {i18n.t('ats.evaluation_forms.form.delete_skill.confirm_cancel')}
              </Button>
              <Button type="negative" submit>
                {i18n.t('ats.evaluation_forms.form.delete_skill.confirm_submit')}
              </Button>
            </Box>
          }
        />
      )}
    </Box>
  )
}

interface QuestionFormProps {
  value: AtsEvaluationFormQuestion
  onChange: (newValue: AtsEvaluationFormQuestion) => void
  onCancel: () => void
  errors: EvaluationFormQuestionError
  isLastQuestion: boolean
  isNew: boolean
}

const QuestionForm = ({
  value,
  onChange,
  onCancel,
  errors,
  isLastQuestion,
  isNew,
}: QuestionFormProps) => {
  const form = useFormik<AtsEvaluationFormQuestion>({
    initialValues: {
      id: value.id,
      text: value.text,
      description: value.description,
    },
    validate(data) {
      const errors: EvaluationFormQuestionError = {}

      if (!data.text) {
        errors.text = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit(data) {
      onChange(data)
    },
  })

  useEffect(() => {
    if (!isEmpty(errors) && !form.dirty) {
      form.setErrors(errors)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmpty(errors), form.dirty])

  return (
    <Form onSubmit={form.handleSubmit} withBackground>
      <TextField
        mandatory
        label={i18n.t('ats.evaluation_forms.form.skill_label')}
        placeholder={i18n.t('ats.evaluation_forms.form.skill_placeholder')}
        value={form.values.text}
        onChange={(text) => form.setFieldValue('text', text)}
        error={form.errors.text}
      />
      <TextArea
        label={i18n.t('ats.evaluation_forms.form.questions_label')}
        placeholder={i18n.t('ats.evaluation_forms.form.questions_placeholder')}
        value={form.values.description ?? ''}
        onChange={(description) => form.setFieldValue('description', description)}
      />

      <Form.Buttons>
        {(!isLastQuestion || !isNew) && (
          <Button onClick={onCancel} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
        )}

        <Button submit>{i18n.t('common.save')}</Button>
      </Form.Buttons>
    </Form>
  )
}
